import { Injectable } from '@angular/core';

export interface CategoryBreakdown {
  key: string;
  name: string;
  maxPossibleScore: number;
  earnedScore: number;
  grade: string;
  gradeColor: string;
  positiveCount: number;
  neutralCount: number;
  negativeCount: number;
  positivePct: number;
  neutralPct: number;
  negativePct: number;
  improvementPct: number;
}

export interface ExperienceResult {
  // Survey counts
  totalCompletedSurveys: number;
  overallPositiveCount?: number | null;
  overallNeutralCount?: number | null;
  overallNegativeCount?: number | null;

  // Experience Summary
  overallPerformance: number | null; // Raw performance value: [-1.0, 1.0]
  normalizedScore: number | null;    // Normalized score: [-3.0, 3.0]
  overallSentiment: string | null;   // Excellent, Good, Average, Poor, Bad
  overallGrade: string | null;       // A, B, C, D, F
  overallGradeColor: string;          // green, yellow, orange, red

  // Experience Breakdown
  totalPossibleScore: number | null;
  accumulatedHappinessScore: number | null;
  categories: CategoryBreakdown[];

  // Satisfaction Analysis (Overall)
  positivePct: number | null;
  neutralPct: number | null;
  negativePct: number | null;
  satisfactionPct: number | null;
  dissatisfactionPct: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class EventExperienceService {

  // Map of keys to display names as specified in the ticket
  private readonly CATEGORY_CONFIGS = [
    { key: 'worth_time_money', name: 'Value / Budget' },
    { key: 'crowd_demographics', name: 'Audience' },
    { key: 'time_setting', name: 'Time ("When")' },
    { key: 'location', name: 'Venue ("Where")' },
    { key: 'theme', name: 'Event Theme' },
    { key: 'host', name: 'Host / Entertainment' },
    { key: 'advertisement', name: 'Advertisement' },
    { key: 'cost_value', name: 'Price' }
  ];

  /**
   * Score mapping for individual responses:
   * Value: 5 -> A (Points: 3.0, Normalized: 3.0)
   * Value: 4 -> B (Points: 2.0, Normalized: 1.0)
   * Value: 3 -> C (Points: 1.0, Normalized: -1.0)
   * Value: 2 -> D (Points: 0.0, Normalized: -3.0)
   * Value: 1 -> F (Points: 0.0, Normalized: -3.0)
   */
  public getScoreDetails(value: number): { grade: string; points: number; normalized: number } {
    switch (value) {
      case 5:
        return { grade: 'A', points: 3.0, normalized: 3.0 };
      case 4:
        return { grade: 'B', points: 2.0, normalized: 1.0 };
      case 3:
        return { grade: 'C', points: 1.0, normalized: -1.0 };
      case 2:
        return { grade: 'D', points: 0.0, normalized: -3.0 };
      case 1:
      default:
        return { grade: 'F', points: 0.0, normalized: -3.0 };
    }
  }

  /**
   * Determine the letter grade for a category's average performance percentage [0.0, 1.0]
   */
  public getAverageGrade(percentage: number): { grade: string; color: string } {
    if (percentage >= 0.9) return { grade: 'A', color: 'green' };
    if (percentage >= 0.8) return { grade: 'B', color: 'green' };
    if (percentage >= 0.7) return { grade: 'C', color: 'yellow' };
    if (percentage >= 0.6) return { grade: 'D', color: 'orange' };
    return { grade: 'F', color: 'red' };
  }

  /**
   * Determine overall sentiment from normalized score
   */
  public getSentiment(normalizedScore: number): string {
    if (normalizedScore >= 2.0) return 'Excellent';
    if (normalizedScore >= 0.8) return 'Good';
    if (normalizedScore >= 0.0) return 'Average';
    if (normalizedScore >= -1.5) return 'Poor';
    return 'Bad';
  }

  /**
   * Calculate Experience Score metrics from completed survey responses
   */
  public calculateExperience(surveys: any[]): ExperienceResult {
    // Filter to only completed surveys
    const completedSurveys = (surveys || []).filter(s => s && s.is_complete && s.status === 'completed');
    const N = completedSurveys.length;

    if (N === 0) {
      return {
        totalCompletedSurveys: 0,
        overallPositiveCount: 0,
        overallNeutralCount: 0,
        overallNegativeCount: 0,
        overallPerformance: null,
        normalizedScore: null,
        overallSentiment: null,
        overallGrade: null,
        overallGradeColor: 'medium',
        totalPossibleScore: null,
        accumulatedHappinessScore: null,
        positivePct: null,
        neutralPct: null,
        negativePct: null,
        satisfactionPct: null,
        dissatisfactionPct: null,
        categories: []
      };
    }

    let overallPositiveCount = 0;
    let overallNeutralCount = 0;
    let overallNegativeCount = 0;
    let accumulatedHappinessScore = 0;

    const categoriesBreakdown: CategoryBreakdown[] = this.CATEGORY_CONFIGS.map(config => {
      let earnedScore = 0;
      let positiveCount = 0;
      let neutralCount = 0;
      let negativeCount = 0;

      completedSurveys.forEach(survey => {
        // Safe access to survey.answers[config.key]
        const answer = survey.answers ? survey.answers[config.key] : null;
        const val = answer ? Number(answer.value) : 3; // Fallback to neutral (3) if missing
        
        const details = this.getScoreDetails(val);
        earnedScore += details.points;

        if (val === 4 || val === 5) {
          positiveCount++;
          overallPositiveCount++;
        } else if (val === 3) {
          neutralCount++;
          overallNeutralCount++;
        } else {
          negativeCount++;
          overallNegativeCount++;
        }
      });

      const categoryPercentage = earnedScore / (N * 3.0);
      const { grade, color } = this.getAverageGrade(categoryPercentage);

      accumulatedHappinessScore += earnedScore;

      return {
        key: config.key,
        name: config.name,
        maxPossibleScore: N * 3.0,
        earnedScore: Number(earnedScore.toFixed(2)),
        grade,
        gradeColor: color,
        positiveCount,
        neutralCount,
        negativeCount,
        positivePct: Number(((positiveCount / N) * 100).toFixed(2)),
        neutralPct: Number(((neutralCount / N) * 100).toFixed(2)),
        negativePct: Number(((negativeCount / N) * 100).toFixed(2)),
        improvementPct: Number((((neutralCount + negativeCount) / N) * 100).toFixed(2))
      };
    });

    const totalPossibleScore = N * 24.0; // 8 categories * 3.0 points max
    const overallPerformance = accumulatedHappinessScore / totalPossibleScore;
    const normalizedScore = (overallPerformance - 0.5) * 6.0;

    const overallSentiment = this.getSentiment(normalizedScore);
    const { grade: overallGrade, color: overallGradeColor } = this.getAverageGrade(overallPerformance);

    const totalAnswers = N * 8;
    const positivePct = Number(((overallPositiveCount / totalAnswers) * 100).toFixed(2));
    const neutralPct = Number(((overallNeutralCount / totalAnswers) * 100).toFixed(2));
    const negativePct = Number(((overallNegativeCount / totalAnswers) * 100).toFixed(2));

    return {
      totalCompletedSurveys: N,
      overallPositiveCount,
      overallNeutralCount,
      overallNegativeCount,
      overallPerformance: Number(overallPerformance.toFixed(4)),
      normalizedScore: Number(normalizedScore.toFixed(2)),
      overallSentiment,
      overallGrade,
      overallGradeColor,
      totalPossibleScore,
      accumulatedHappinessScore: Number(accumulatedHappinessScore.toFixed(2)),
      categories: categoriesBreakdown,
      positivePct,
      neutralPct,
      negativePct,
      satisfactionPct: positivePct,
      dissatisfactionPct: negativePct
    };
  }
}
